import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Settings → Sync (WS-7 Slice 2, D48). Admin-editable config.dmstack_sync — record
// filters + field toggles + an arbitrary DMStack→CRM field-map + enable + max_batch.
// Config-engine (no redeploy); writes gated by manage_campaigns_automations (RLS).
// The DMStack pull agent fetches this via sync-pull-request and builds each intake
// payload from the field_map. Guardrails: external_id required (→ case_number);
// source_channel is always "dmstack"; the phones block is assembled structurally
// (not in the map). The DEFAULT map reproduces Slice 1 exactly.

// Catalogs — MUST match the DMStack agent (sync_agent/deliver_crm). base.* = the
// deliver_batchleads _build_row parsed output (default map uses these).
const SOURCE_CATALOG = [
  "case_number", "case_type", "county", "filed_date",
  "base.first_name", "base.last_name", "base.email",
  "base.property_street", "base.property_city", "base.property_state", "base.property_zip",
  "base.mailing_street", "base.mailing_city", "base.mailing_state", "base.mailing_zip",
  "bcpa_owner_name", "decision_maker_name", "verified_address",
  "petitioner.first_name", "petitioner.last_name", "petitioner.full_name", "petitioner.email",
  "petitioner.address", "petitioner.city", "petitioner.state", "petitioner.zip",
  "mailing_address", "mailing_city", "mailing_state", "mailing_zip",
  "bcpa_mailing_address", "bcpa_mailing_city", "bcpa_mailing_state", "bcpa_mailing_zip",
  "emails[0]",
];
const TARGET_CATALOG = [
  "external_id", "lead_type", "first_name", "last_name", "email",
  "address_line1", "address_line2", "city", "state", "zip", "county", "notes", "intake.*",
];
const TRANSFORMS = [
  "as_is", "title", "upper", "split_name.first", "split_name.last",
  "parse_address.street", "parse_address.city", "parse_address.state", "parse_address.zip",
];
const CASE_TYPES = ["probate", "foreclosure", "divorce"];
const COUNTIES = ["Broward", "Miami-Dade", "Palm Beach"];
const CADENCES = ["hourly", "every_2h", "every_4h", "business_hours"];

type Rule = { source: string; target: string; transform: string };
type Schedule = { enabled: boolean; cadence: string; tz: string; times: string[] };
type SyncConfig = {
  enabled: boolean;
  filters: { case_types: string[]; counties: string[]; min_phone_score: number | null; filed_since: string | null };
  fields: { max_phones_per_lead: number | null; include_dnc: boolean; include_email: boolean };
  field_map: Rule[];
  max_batch: number;
  schedule?: Schedule;
};
type SyncRun = {
  id: string; ran_at: string; trigger: string | null; candidates: number | null;
  pushed: number | null; skipped_known: number | null; errors: number | null;
  duration_s: number | null; error_detail: string | null;
};
const DEFAULT_SCHEDULE: Schedule = { enabled: false, cadence: "business_hours", tz: "America/New_York", times: ["08:00", "11:00", "14:00", "17:00"] };

const SyncPage = () => {
  const notify = useNotify();
  const [cfg, setCfg] = useState<SyncConfig | null>(null);
  const [runs, setRuns] = useState<SyncRun[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSupabaseClient().from("sync_runs").select("*").order("ran_at", { ascending: false }).limit(20)
      .then(({ data }) => setRuns((data as SyncRun[]) ?? []));
    getSupabaseClient().from("config").select("value").eq("key", "dmstack_sync").maybeSingle()
      .then(({ data }) => setCfg((data as { value?: SyncConfig } | null)?.value ?? null));
  }, []);

  if (!cfg) return <div className="p-2 text-sm text-muted-foreground">Loading sync config…</div>;

  const set = (patch: Partial<SyncConfig>) => setCfg((c) => (c ? { ...c, ...patch } : c));
  const setFilter = (p: Partial<SyncConfig["filters"]>) => set({ filters: { ...cfg.filters, ...p } });
  const setFields = (p: Partial<SyncConfig["fields"]>) => set({ fields: { ...cfg.fields, ...p } });
  const sched = cfg.schedule ?? DEFAULT_SCHEDULE;
  const setSchedule = (p: Partial<Schedule>) => set({ schedule: { ...sched, ...p } });
  const toggleIn = (list: string[], v: string) => list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const setRule = (i: number, p: Partial<Rule>) =>
    set({ field_map: cfg.field_map.map((r, idx) => (idx === i ? { ...r, ...p } : r)) });
  const addRule = () => set({ field_map: [...cfg.field_map, { source: "case_number", target: "notes", transform: "as_is" }] });
  const rmRule = (i: number) => set({ field_map: cfg.field_map.filter((_, idx) => idx !== i) });

  const save = async () => {
    // Guardrail validation: targets known; external_id required (→ case_number).
    const validTarget = (t: string) =>
      (TARGET_CATALOG.includes(t) && t !== "intake.*") || (t.startsWith("intake.") && t.length > "intake.".length);
    let fm = cfg.field_map;
    const bad = fm.find((r) => !validTarget(r.target));
    if (bad) { notify(`Invalid target "${bad.target}" — pick a target field or set an intake.<key>.`, { type: "error" }); return; }
    if (!fm.some((r) => r.target === "external_id")) {
      fm = [{ source: "case_number", target: "external_id", transform: "as_is" }, ...fm];
      notify("external_id was missing — defaulted to case_number.", { type: "info" });
    }
    setSaving(true);
    const { error } = await getSupabaseClient().from("config").update({ value: { ...cfg, field_map: fm } }).eq("key", "dmstack_sync");
    setSaving(false);
    if (error) { notify(`Save failed: ${error.message}`, { type: "error" }); return; }
    notify("Sync config saved", { type: "success" });
  };

  const sel = "border rounded px-2 py-1.5 text-sm bg-background";
  return (
    <div className="p-2 max-w-4xl">
      <h1 className="text-xl font-semibold">DMStack sync</h1>
      <div className="text-sm text-muted-foreground mb-3">
        Controls which DMStack cases the pull agent sends to the CRM, and how their fields map. The phones block
        (scored, DNC-flagged, litigators excluded) is always assembled automatically. Ships off — enable to opt in.
      </div>

      <Card className="mb-3">
        <CardContent className="flex flex-wrap items-center gap-4 py-3">
          <label className="flex items-center gap-2">
            <Switch checked={cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} />
            <span className="text-sm font-medium">Enabled</span>
            {cfg.enabled ? <Badge className="bg-green-100 text-green-800 text-[10px]">pull active</Badge>
                         : <Badge variant="secondary" className="text-[10px]">off</Badge>}
          </label>
          <label className="flex items-center gap-2 text-sm">Max batch
            <Input type="number" className="w-24" value={cfg.max_batch} onChange={(e) => set({ max_batch: +e.target.value || 0 })} /></label>
          <Button className="ml-auto" disabled={saving} onClick={save}>Save</Button>
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader>
          <CardTitle className="text-base">Schedule</CardTitle>
          <div className="text-xs text-muted-foreground">
            A launch-agent heartbeat on the Mac carries no schedule of its own — every beat it reads this config and
            pulls only when the master switch above is on, auto-fire is on, and a slot is due. Turning auto-fire off
            (or the master switch above) stops all scheduled pulls immediately.
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2">
              <Switch checked={sched.enabled} onCheckedChange={(v) => setSchedule({ enabled: v })} />
              <span className="text-sm font-medium">Auto-fire</span>
              {sched.enabled ? <Badge className="bg-green-100 text-green-800 text-[10px]">scheduled</Badge>
                             : <Badge variant="secondary" className="text-[10px]">manual only</Badge>}
            </label>
            <label className="flex items-center gap-2 text-sm">Cadence
              <select className={sel} value={sched.cadence} onChange={(e) => setSchedule({ cadence: e.target.value })}>
                {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="flex items-center gap-2 text-sm">Timezone
              <Input className="w-44" value={sched.tz} onChange={(e) => setSchedule({ tz: e.target.value })} /></label>
          </div>
          {sched.cadence === "business_hours" && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Business-hours slots (HH:MM, {sched.tz})</div>
              <div className="flex flex-wrap items-center gap-2">
                {sched.times.map((t, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <Input className="w-24" value={t} onChange={(e) => setSchedule({ times: sched.times.map((x, idx) => idx === i ? e.target.value : x) })} placeholder="HH:MM" />
                    <Button size="sm" variant="ghost" onClick={() => setSchedule({ times: sched.times.filter((_, idx) => idx !== i) })} title="Remove"><Trash2 className="h-4 w-4" /></Button>
                  </span>
                ))}
                <Button size="sm" variant="outline" onClick={() => setSchedule({ times: [...sched.times, "12:00"] })}>+ slot</Button>
              </div>
            </div>
          )}
          {sched.cadence !== "business_hours" && (
            <div className="text-xs text-muted-foreground">
              Fires on the {sched.cadence.replace("every_", "every ").replace("h", " hours").replace("hourly", "hour")} boundary while auto-fire is on. Times list applies only to business_hours.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader><CardTitle className="text-base">Record filters</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Case types</div>
            <div className="flex gap-3">{CASE_TYPES.map((t) => (
              <label key={t} className="flex items-center gap-1 text-sm capitalize">
                <Checkbox checked={cfg.filters.case_types.includes(t)} onCheckedChange={() => setFilter({ case_types: toggleIn(cfg.filters.case_types, t) })} />{t}
              </label>))}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Counties</div>
            <div className="flex gap-3">{COUNTIES.map((c) => (
              <label key={c} className="flex items-center gap-1 text-sm">
                <Checkbox checked={cfg.filters.counties.includes(c)} onCheckedChange={() => setFilter({ counties: toggleIn(cfg.filters.counties, c) })} />{c}
              </label>))}</div>
          </div>
          <div className="flex gap-3 flex-wrap items-end">
            <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Min phone score</span>
              <Input type="number" className="w-28" value={cfg.filters.min_phone_score ?? ""} onChange={(e) => setFilter({ min_phone_score: e.target.value === "" ? null : +e.target.value })} placeholder="any" /></label>
            <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Filed since</span>
              <Input type="date" className="w-40" value={cfg.filters.filed_since ?? ""} onChange={(e) => setFilter({ filed_since: e.target.value || null })} /></label>
          </div>
          <div className="flex gap-4 flex-wrap items-end border-t pt-2">
            <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Max phones / lead</span>
              <Input type="number" className="w-28" value={cfg.fields.max_phones_per_lead ?? ""} onChange={(e) => setFields({ max_phones_per_lead: e.target.value === "" ? null : +e.target.value })} placeholder="all" /></label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={cfg.fields.include_dnc} onCheckedChange={(v) => setFields({ include_dnc: !!v })} />Include DNC numbers (stored, suppressed)</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={cfg.fields.include_email} onCheckedChange={(v) => setFields({ include_email: !!v })} />Include email</label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Field map <span className="text-xs text-muted-foreground font-normal">(DMStack source → CRM target)</span></CardTitle>
          <Button size="sm" variant="outline" onClick={addRule}><Plus className="mr-1 h-4 w-4" /> Add rule</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-xs text-muted-foreground">
            <b>external_id</b> is required (defaults to <code>case_number</code>). <b>phones</b>, <b>source_channel</b> are handled
            automatically. <code>base.*</code> sources are the parsed name/property from DMStack's row builder — the default map uses them.
          </div>
          {cfg.field_map.map((r, i) => {
            const isIntake = r.target.startsWith("intake.");
            return (
              <div key={i} className="flex items-center gap-2 text-sm flex-wrap border-b pb-2 last:border-0">
                <select className={sel} value={r.source} onChange={(e) => setRule(i, { source: e.target.value })}>
                  {SOURCE_CATALOG.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <span className="text-muted-foreground">→</span>
                <select className={sel} value={isIntake ? "intake.*" : r.target} onChange={(e) => setRule(i, { target: e.target.value === "intake.*" ? "intake.custom_field" : e.target.value })}>
                  {TARGET_CATALOG.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {isIntake && (
                  <Input className="w-40" value={r.target.replace(/^intake\./, "")} onChange={(e) => setRule(i, { target: `intake.${e.target.value.replace(/\s+/g, "_")}` })} placeholder="custom key" />
                )}
                <select className={sel} value={r.transform} onChange={(e) => setRule(i, { transform: e.target.value })}>
                  {TRANSFORMS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => rmRule(i)} title="Remove"><Trash2 className="h-4 w-4" /></Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader>
          <CardTitle className="text-base">Recent sync runs</CardTitle>
          <div className="text-xs text-muted-foreground">Last 20 actual pulls (scheduled or manual). No-op heartbeats write nothing.</div>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-sm text-muted-foreground">No runs recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-1 pr-3">When</th><th className="py-1 pr-3">Trigger</th>
                    <th className="py-1 pr-3 text-right">Candidates</th><th className="py-1 pr-3 text-right">Pushed</th>
                    <th className="py-1 pr-3 text-right">Known</th><th className="py-1 pr-3 text-right">Errors</th>
                    <th className="py-1 pr-3 text-right">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-1 pr-3 whitespace-nowrap">{new Date(r.ran_at).toLocaleString()}</td>
                      <td className="py-1 pr-3">
                        <Badge variant={r.trigger === "scheduled" ? "default" : "secondary"} className="text-[10px]">{r.trigger ?? "—"}</Badge>
                      </td>
                      <td className="py-1 pr-3 text-right">{r.candidates ?? "—"}</td>
                      <td className="py-1 pr-3 text-right">{r.pushed ?? "—"}</td>
                      <td className="py-1 pr-3 text-right">{r.skipped_known ?? "—"}</td>
                      <td className={`py-1 pr-3 text-right ${r.errors ? "text-red-600 font-medium" : ""}`}>{r.errors ?? "—"}</td>
                      <td className="py-1 pr-3 text-right">{r.duration_s != null ? `${r.duration_s}s` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

SyncPage.path = "/settings/sync";
export default SyncPage;
